import { ArrowBackIosNew, Check, Close } from "@mui/icons-material";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { isEmpty } from "lodash";
import { forwardRef, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { useSendPasswordResetEmailMutation } from "../../services/projects/projectsApi";
import { seicDark } from "../../shared-components/src/gnb-theme-ts";

type ForgotPasswordProps = {
  onBack: () => void;
  signInEmail?: string;
};

const ForgotPassword = forwardRef(
  ({ onBack, signInEmail, ...props }: ForgotPasswordProps, ref) => {
    const [emailSent, setEmailSent] = useState("");
    const [email, setEmail] = useState(signInEmail || "");
    const [errors, setErrors] = useState<string[]>([]);

    const [
      sendEmailResetLink,
      { isLoading, error },
    ] = useSendPasswordResetEmailMutation();

    const validate = () => {
      const _errors: Record<string, string> = {};

      if (!email) {
        _errors.email = "Email required.";
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
        _errors.email = "Invalid email.";
      }
      setErrors(Object.values(_errors));
      return isEmpty(_errors);
    };

    const handleSendResetLink = async () => {
      if (!validate()) {
        return;
      }
      try {
        await sendEmailResetLink(email).unwrap();
        setEmailSent(email.trim());
      } catch (e) {
        console.log(e);
      }
    };

    return (
      <Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendResetLink();
          }}
        >
          <Typography
            variant="h2"
            sx={{ fontSize: 32, fontWeight: 400, mb: "24px" }}
          >
            Forgot Password
          </Typography>
          <TextField
            label="Email"
            name="email"
            variant="filled"
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            className="forgot-password-email"
            sx={{ mb: "16px" }}
          />

          {(errors.length || !!error) && !isLoading && (
            <Box
              sx={{
                mb: "24px",
                textAlign: "left",
                border: "solid 1px",
                borderColor: "error.main",
                borderRadius: "4px",
                p: "8px 16px",
                lineHeight: "16px",
                "& a:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {errors.map((e) => (
                <Typography
                  key={e}
                  variant="body1"
                  sx={{
                    fontSize: 12,
                    color: "error.main",
                    fontWeight: 400,
                    lineHeight: "16px",
                  }}
                >
                  {e}
                </Typography>
              ))}
              {error && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      fontWeight: 400,
                    }}
                  >
                    <Box component={"span"} sx={{ color: "error.main" }}>
                      Invalid credentials.{" "}
                    </Box>
                    Not a customer yet?{" "}
                  </Typography>
                  <a
                    target="_blank"
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      padding: 0,
                      textTransform: "none",
                      color: seicDark.primary,
                    }}
                    href="https://www.stryten.com/contact/"
                  >
                    Contact us
                  </a>
                </Box>
              )}
            </Box>
          )}

          <LoadingButton
            size="large"
            loading={isLoading}
            variant="contained"
            fullWidth
            type="submit"
            sx={{ mb: "8px" }}
          >
            Send reset link
          </LoadingButton>

          {emailSent && !isLoading && !error && (
            <Box
              sx={{
                border: "solid 1px",
                borderColor: "primary.main",
                p: "12px 16px",
                display: "flex",
                flexWrap: "wrap",
                borderRadius: "4px",
                alignItems: "center",
              }}
            >
              <Check
                sx={{
                  alignSelf: "flex-start",
                  color: "primary.main",
                  mr: "8px",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  Email was sent to
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  fontWeight={"bold"}
                >
                  {emailSent}
                </Typography>
              </Box>
              <IconButton>
                <Close
                  sx={{ fontSize: "20px", color: "text.disabled" }}
                  onClick={() => setEmailSent(false)}
                />
              </IconButton>
            </Box>
          )}
          <Box
            color="inherit"
            className=""
            sx={{
              fontSize: 14,
              fontWeight: 400,
              color: "text.secondary",
              mt: "24px",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
            onClick={onBack}
          >
            <ArrowBackIosNew sx={{ fontSize: 16 }} /> Back to login
          </Box>
        </form>
      </Box>
    );
  }
);

export default ForgotPassword;
